import { Picture as PictureComponent } from '@tv2/lib-offspring/components/primitives/Picture';
import { LinkButton } from '@tv2/lib-offspring/components/link/Link';
import { toImgix } from '@tv2/lib-offspring/libs/image/imgix';
import { createPicture, Picture } from '@tv2/lib-offspring/libs/image/picture';
import { ReactNode } from 'react';

function Error({
  title,
  body,
  link,
}: {
  title: string;
  body: string;
  link: { title: string; url: string };
}) {
  const bg = toImgix('https://play-assets.imgix.net/bg-404.jpg', {
    blur: 300,
    dpr: 0.5,
    fit: 'clip',
    h: 1080,
  });
  const images = {
    768: 'https://play-assets.imgix.net/bg-404.jpg?rect=886,0,2520,2160',
    992: 'https://play-assets.imgix.net/bg-404.jpg?rect=0,0,3840,2160',
    1200: 'https://play-assets.imgix.net/bg-404.jpg?rect=0,0,3840,2160',
    1440: 'https://play-assets.imgix.net/bg-404.jpg?rect=0,0,3840,2160',
    1920: 'https://play-assets.imgix.net/bg-404.jpg?rect=0,269,3840,1646',
  };

  const pic = createPicture({
    baseUrl: images,
    modifications: ['RightCornerMark'],
    aspectRatio: undefined,
    dprs: undefined,
    breakPoints: [992, 1200, 1440, 1920],
  });

  const mob = createPicture({
    baseUrl: 'https://play-assets.imgix.net/404-mobile.jpg?rect=40,0,1312,1124',
    modifications: [],
    aspectRatio: '7 / 6',
  });

  return (
    <div className="relative">
      <SquareImage picture={mob} />
      <Background picture={pic} backgroundImage={bg} />
      <div className="deck flex flex-col space-y-[275px] 768:space-y-0">
        <div className="relative mb-8 1440:mb-36 w-col-12 1200:w-col-6 1440:w-col-5 1920:w-col-5 768:min-h-[700px]">
          <div className="mt-4 768:mt-0 768:pt-[340px] 992:pt-[120px] relative">
            <div className="mb-4 w-col-12 1200:w-col-6 1440:w-col-5 1920:w-col-4">
              <div className="flex flex-col col-12">
                <h1 className="text-headline-2xs 768:text-headline-xs  text-white">{title}</h1>
                <p className="text-white text-legacy-body-s">{body}</p>
                <div className="mt-11">
                  <LinkButton href={link.url} color="primary">
                    {link.title}
                  </LinkButton>
                </div>
              </div>
            </div>
          </div>
        </div>
        <NeedHelp />
      </div>
    </div>
  );
}

function NeedHelp() {
  return (
    <div className="flex flex-col max-w-[315px] space-y-1">
      <h2 className="text-subheadline-s text-white">Brug for hjælp?</h2>
      <p className="text-legacy-body-xs text-neutral-300">
        Har du brug for vejledning? Besøg vores supportside{' '}
        <a href="https://play.kundeservice.tv2.dk/hc/da" className="underline">
          kundecenter
        </a>
      </p>
    </div>
  );
}

function SquareImage({ picture }: { picture: Picture }) {
  return (
    <div
      className="768:hidden bg-midnight-900 relative"
      style={{
        aspectRatio: picture?.aspectRatio ?? '7 / 6',
      }}
    >
      <div>{picture && <PictureComponent picture={picture} />}</div>
      <div className="bg-gradient-to-t from-midnight absolute top-1/2 bottom-0 inset-x-0 -mb-[1px]" />
    </div>
  );
}

function Background({ backgroundImage, picture }: { backgroundImage: string; picture: Picture }) {
  return (
    <div className="hidden 768:block relative">
      <div className="absolute w-full top-0 right-0 bg-midnight-600 h-[1000px] -z-10">
        <img className="w-full h-full" src={backgroundImage} alt="" />
        <div
          className="absolute inset-0 bg-gradient-to-t from-midnight"
          style={{
            backgroundColor: 'rgba(0, 5, 35, 0.54)',
          }}
        />
      </div>
      <PictureComponent className="absolute right-0 768:w-col-10" picture={picture} />
    </div>
  );
}

export function CatchAllError(): ReactNode {
  return (
    <Error
      title="Ups... der er sket en uventet fejl."
      body="Prøv igen om lidt eller kontakt kundeservice."
      link={{ title: 'Gå til forside', url: '/forside' }}
    />
  );
}

export function Four04(): ReactNode {
  return (
    <Error
      title="Ups... vi kan ikke finde siden!"
      body="Det kan skyldes, at linket ikke virker, eller at siden ikke længere findes."
      link={{ title: 'Gå til forside', url: '/forside' }}
    />
  );
}
